import { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { formatSqlQuery } from "../../utils/sqlFormatter";

import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";

import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import { useNavigate, useParams } from "react-router-dom";
import { format, isToday, isYesterday } from "date-fns";

import { StorageHelper } from "../../utils/storageHelper";
import { useCollaboration } from "../../hooks/useCollaboration";
import { SOCKET_EVENTS } from "../../constants";
import ShareModal from "../../components/Share/ShareModal";

import SearchSavedQuerySidebar from "./SearchSavedQuerySidebar";

import {
  RootBox,
  ChatArea,
  ContentContainer,
  ChatShell,
  HeaderRow,
  UsersTitle,
  QueryBar,
  ChatCard,
  ChatMsgRow,
  ChatMsgAvatar,
  ChatMsgBox,
  ChatMsgCol,
  ChatMsgPaper,
  ChatMsgText,
  ChatMsgTime,
  InputBar,
  InputBox,
  InputShell,
  InputField,
  IconBtn,
  SendButton,
  TypingIndicator,
  OwnerPill,
  OwnerAvatar,
  OwnerName,
  OwnerStatus,
  StatusDotInline,
  ChatMsgSender,
  POPOVER_RIGHT_GAP_PX,
  POPOVER_WIDTH_PX,
} from "./Collaboration.style";

const WEBSOCKET_URL = `${process.env.REACT_APP_WEBSOCKET_URL || ""}`;
const socket = io(WEBSOCKET_URL, {
  transports: ["websocket"],
  path: "/socket.io",
});

const PASTELS = [
  "#E3F2FD",
  "#FCE7F3",
  "#E8F5E9",
  "#FFF3E0",
  "#F3E8FF",
  "#E0F2F1",
  "#F1F5F9",
];
const pastelFrom = (str = "") => {
  const s = String(str || "u");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return PASTELS[Math.abs(h) % PASTELS.length];
};

function PeoplePopover({
  anchorEl,
  open,
  onClose,
  members = [],
  connectedUsers = [],
  currentUsername = "",
  containerRef,
  onAddPeople,
  isOwner = false,
}) {
  const organizerUsername = members?.[0]?.username;
  const THEME = { primary: "rgb(44, 52, 123)", primaryRgba: (a) => `rgba(44, 52, 123, ${a})` };
  const navigate = useNavigate();
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      container={containerRef?.current || undefined}
      disablePortal
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PopperProps={{
        modifiers: [
          { name: "offset", options: { offset: [0, 8] } },
          {
            name: "preventOverflow",
            options: {
              boundary: containerRef?.current || undefined,
              padding: POPOVER_RIGHT_GAP_PX,
              altAxis: true,
              tether: true,
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["top-end", "bottom-start", "top-start"],
              boundary: containerRef?.current || undefined,
              padding: POPOVER_RIGHT_GAP_PX,
            },
          },
        ],
      }}
      PaperProps={{
        sx: {
          zIndex: 1500,
          mt: 0,
          mr: `${POPOVER_RIGHT_GAP_PX}px`,
          width: POPOVER_WIDTH_PX,
          borderRadius: 2,
          border: `1px solid ${THEME.primaryRgba(0.15)}`,
          boxShadow: `0 4px 20px ${THEME.primaryRgba(0.08)}, 0 2px 8px rgba(0,0,0,0.06)`,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ px: 1.25, py: 0.75, borderBottom: `1px solid ${THEME.primaryRgba(0.12)}`, bgcolor: THEME.primaryRgba(0.03), borderRadius: "2px 2px 0 0" }}>
        <Typography
          variant="subtitle2"
          sx={{ color: THEME.primary, fontWeight: 700, fontSize: "0.85rem" }}
        >
          Members ({members?.length || 0})
        </Typography>
      </Box>

      <Box
        sx={{
          maxHeight: 280,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "#f1f5f9", borderRadius: 3 },
          "&::-webkit-scrollbar-thumb": {
            background: THEME.primaryRgba(0.35),
            borderRadius: 3,
            "&:hover": { background: THEME.primaryRgba(0.5) },
          },
        }}
      >
        <List sx={{ py: 0.25 }}>
        {members.map((m, i) => {
          const isOnline = connectedUsers.includes(m.username);
          const isOrganizer = m.username === organizerUsername;
          const isYou = m.username === currentUsername;
          const initials = (
            m.first_name?.[0] ||
            m.username?.[0] ||
            "U"
          ).toUpperCase();

          const fullName = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username;
          return (
            <ListItem key={m.username || i} sx={{ px: 1.25, py: 0.5 }}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Tooltip title={fullName} placement="top">
                <Box sx={{ position: "relative", width: 36, height: 36 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: pastelFrom(m.username || m.first_name),
                      color: "#0f172a",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {initials}
                  </Avatar>

                  {isOnline ? (
                    <Box
                      sx={{
                        position: "absolute",
                        right: 2,
                        bottom: 2,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        border: "2px solid #fff",
                        backgroundColor: "#22c55e",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        right: 2,
                        bottom: 2,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        border: "2px solid #fff",
                        backgroundColor: "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 8,
                        fontWeight: 700,
                        color: "#fff",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </Box>
                  )}
                </Box>
                </Tooltip>
              </ListItemAvatar>

              <Tooltip title={fullName} placement="top">
              <ListItemText
                sx={{ flex: 1, minWidth: 0 }}
                primary={
                  <Typography
                    sx={{ fontWeight: 700, color: "#111827", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {fullName}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: "flex", gap: 1, mt: 0.05 }}>
                    {isOrganizer ? (
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>
                        Organizer
                      </Typography>
                    ) : isYou ? (
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>
                        You
                      </Typography>
                    ) : null}
                  </Box>
                }
                primaryTypographyProps={{ sx: { mb: 0 } }}
              />
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
      </Box>

      <Divider sx={{ borderColor: THEME.primaryRgba(0.1) }} />
      <Box sx={{ py: 0.5 }}>
        {isOwner && (
          <Box
            sx={{
              px: 1.25,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: 8,
              mx: 0.5,
              "&:hover": { 
                background: "rgba(44, 52, 123, 0.06)",
              },
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onClick={onAddPeople}
          >
            <PersonAddAlt1RoundedIcon sx={{ fontSize: 18, color: "rgb(44, 52, 123)" }} />
            <Typography sx={{ color: "rgb(44, 52, 123)", fontWeight: 600, fontSize: "0.8rem" }}>Add Member</Typography>
          </Box>
        )}
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            mx: 0.5,
            borderRadius: 8,
            "&:hover": { background: "rgba(239, 68, 68, 0.06)" },
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          <LogoutRoundedIcon sx={{ fontSize: 20, color: "#ef4444" }} />
          <Typography sx={{ color: "#ef4444" }}>Leave</Typography>
        </Box>
      </Box>
    </Popover>
  );
}

export const Collaboration = () => {
  const params = useParams();
  const navigate = useNavigate();

  const userStore = StorageHelper.get("userDetails")?.user_id
    ? StorageHelper.get("userDetails")
    : StorageHelper.get("userInfo");

  const [channelId, setChannelId] = useState("1");
  const [channelCode, setChannelCode] = useState(
    `${window.location.hostname}_${params?.channel_code || ""}`
  );

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [typingUsers, setTypingUsers] = useState({});
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [globalOnlineUsers, setGlobalOnlineUsers] = useState([]);

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [selectedQuery, setSelectedQuery] = useState("");
  const [selectedChannelName, setSelectedChannelName] = useState("");

  const [myChannels, setMyChannels] = useState([]);
  const [channelMembers, setChannelMembers] = useState([]);

  // popover control
  const [peopleOpenState, setPeopleOpenState] = useState(false);
  const [showConversation, setShowConversation] = useState(true);

  // ShareModal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [addingMembersLoading, setAddingMembersLoading] = useState(false);

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Reserve space on the right when popover is open (so SQL wraps)
  const SQL_RIGHT_RESERVE = POPOVER_WIDTH_PX + POPOVER_RIGHT_GAP_PX + 8;

  const messagesEndRef = useRef(null);
  const shellRef = useRef(null);
  const personIconRef = useRef(null); // anchor for PeoplePopover (person icon in header)

  const { getMyChannels, getChannelMessages, addMembersToChannel } =
    useCollaboration();

  // data
  const fetchChannelMessages = useCallback(
    async (id) => {
      try {
        const res = await getChannelMessages(String(id));
        if (res?.success) setMessages(res.data || []);
      } catch (e) {
        console.error("getChannelMessages error:", e);
      }
    },
    [getChannelMessages]
  );

  const loadChannels = useCallback(async () => {
    try {
      const mine = await getMyChannels(String(userStore?.user_id || ""));
      if (mine?.success && Array.isArray(mine.data) && mine.data.length) {
        setMyChannels(mine.data);
      }
    } catch (e) {
      console.error("getMyChannels error:", e);
    }
  }, [getMyChannels, userStore?.user_id]);

  const bootstrapChannels = useCallback(async () => {
    try {
      const mine = await getMyChannels(String(userStore?.user_id || ""));
      if (mine?.success && Array.isArray(mine.data) && mine.data.length) {
        setMyChannels(mine.data);

        // Check if there's a channel_code in the URL params
        const urlChannelCode = params?.channel_code;
        let targetIndex = 0;
        let targetChannel = mine.data[0];

        if (urlChannelCode) {
          // Find the channel matching the URL param
          const foundIndex = mine.data.findIndex(
            (ch) => ch.channel_code === urlChannelCode
          );
          if (foundIndex !== -1) {
            targetIndex = foundIndex;
            targetChannel = mine.data[foundIndex];
          }
        }

        setSelectedSectionIndex(targetIndex);

        navigate("/collaboration/" + targetChannel.channel_code);

        setChannelId(targetChannel.channel_id);
        setChannelCode(`${window.location.hostname}_${targetChannel.channel_code}`);
        setSelectedChannelName(targetChannel.channel_name);
        setSelectedQuery(targetChannel.query);
        setChannelMembers(targetChannel?.channel_members || []);

        fetchChannelMessages(targetChannel.channel_id);
        setShowConversation(true);
      }
    } catch (e) {
      console.error("getMyChannels error:", e);
    }
  }, [getMyChannels, navigate, fetchChannelMessages, userStore?.user_id, params?.channel_code]);

  useEffect(() => {
    bootstrapChannels();
  }, []);

  // Refresh channel list when user changes, but don't change selected channel
  useEffect(() => {
    if (userStore?.user_id) {
      loadChannels();
    }
  }, [userStore?.user_id, loadChannels]);

  // Handle URL parameter changes (when user navigates to different channel)
  useEffect(() => {
    if (params?.channel_code && myChannels.length > 0) {
      const targetChannel = myChannels.find(
        (channel) => channel.channel_code === params.channel_code
      );

      if (targetChannel) {
        const channelIndex = myChannels.findIndex(
          (ch) => ch.channel_id === targetChannel.channel_id
        );

        setSelectedSectionIndex(channelIndex);
        setChannelId(targetChannel.channel_id);
        setChannelCode(
          `${window.location.hostname}_${targetChannel.channel_code}`
        );
        setSelectedChannelName(targetChannel.channel_name);
        setSelectedQuery(targetChannel.query);
        setChannelMembers(targetChannel.channel_members || []);
        fetchChannelMessages(targetChannel.channel_id);
      }
    }
  }, [params?.channel_code, myChannels, fetchChannelMessages]);

  // Global collaboration socket connection (runs once when component mounts)
  useEffect(() => {
    socket.emit(SOCKET_EVENTS.JOIN_COLLABORATION, {
      username: userStore?.username,
    });

    // Listen for global collaboration events
    socket.on(SOCKET_EVENTS.GLOBAL_USER_LIST, (users) =>
      setGlobalOnlineUsers(users || [])
    );
    socket.on(SOCKET_EVENTS.GLOBAL_USER_ONLINE, (username) =>
      setGlobalOnlineUsers((p) => [...p, username])
    );
    socket.on(SOCKET_EVENTS.GLOBAL_USER_OFFLINE, (username) =>
      setGlobalOnlineUsers((p) => p.filter((u) => u !== username))
    );

    return () => {
      socket.emit(SOCKET_EVENTS.LEAVE_COLLABORATION, {
        username: userStore?.username,
      });
      socket.off(SOCKET_EVENTS.GLOBAL_USER_LIST);
      socket.off(SOCKET_EVENTS.GLOBAL_USER_ONLINE);
      socket.off(SOCKET_EVENTS.GLOBAL_USER_OFFLINE);
    };
  }, [userStore?.username]);

  // Channel-specific socket connection
  useEffect(() => {
    socket.emit(SOCKET_EVENTS.JOIN, {
      username: userStore?.username,
      channel_code: channelCode,
    });

    const msgEvt = SOCKET_EVENTS.MESSAGE(channelCode);
    const typingEvt = SOCKET_EVENTS.TYPING(channelCode);
    const listEvt = SOCKET_EVENTS.USER_LIST(channelCode);
    const joinEvt = SOCKET_EVENTS.USER_JOINED(channelCode);
    const leftEvt = SOCKET_EVENTS.USER_LEFT(channelCode);

    socket.on(msgEvt, async () => {
      await fetchChannelMessages(channelId);
    });

    socket.on(typingEvt, ({ username }) => {
      setTypingUsers((prev) => {
        const updated = { ...prev };
        clearTimeout(updated[username]);
        updated[username] = setTimeout(() => {
          setTypingUsers((cur) => {
            const copy = { ...cur };
            delete copy[username];
            return copy;
          });
        }, 2000);
        return updated;
      });
    });

    socket.on(listEvt, (users) => setConnectedUsers(users || []));
    socket.on(joinEvt, (username) =>
      setConnectedUsers((p) => [...p, username])
    );
    socket.on(leftEvt, (username) =>
      setConnectedUsers((p) => p.filter((u) => u !== username))
    );

    return () => {
      socket.emit(SOCKET_EVENTS.LEAVE, {
        username: userStore?.username,
        channel_code: channelCode,
      });
      socket.off(msgEvt);
      socket.off(typingEvt);
      socket.off(listEvt);
      socket.off(joinEvt);
      socket.off(leftEvt);

      Object.values(typingUsers).forEach((t) => clearTimeout(t));
    };
  }, [channelCode, channelId, userStore?.username, fetchChannelMessages]);

  useEffect(() => {
    if (channelId) fetchChannelMessages(channelId);
  }, [channelId, fetchChannelMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // helpers
  const getDisplayName = useCallback(
    (identifier) => {
      if (!identifier) return "";

      const member =
        channelMembers.find(
          (m) =>
            m.username === identifier ||
            m.email === identifier ||
            String(m.user_id) === String(identifier)
        ) || null;

      if (member) {
        const fullName = `${member.first_name || ""} ${
          member.last_name || ""
        }`.trim();
        if (fullName) return fullName;
      }

      if (
        identifier === userStore?.username ||
        identifier === userStore?.email ||
        String(identifier) === String(userStore?.user_id)
      ) {
        const fullName = `${userStore?.first_name || ""} ${
          userStore?.last_name || ""
        }`.trim();
        if (fullName) return fullName;
      }

      return identifier;
    },
    [
      channelMembers,
      userStore?.username,
      userStore?.email,
      userStore?.user_id,
      userStore?.first_name,
      userStore?.last_name,
    ]
  );

  const channelOwner =
    channelMembers && channelMembers.length > 0 ? channelMembers[0] : null;
  const ownerOnline =
    channelOwner && globalOnlineUsers.includes(channelOwner.username);

  // Check if current user is the owner of the current channel
  const currentChannel = myChannels[selectedSectionIndex];
  const isChannelOwner = currentChannel?.is_owner || false;

  const handleSend = useCallback(() => {
    if (!newMessage.trim()) return;

    const payload = {
      sender_username: userStore?.username,
      first_name: userStore?.first_name,
      last_name: userStore?.last_name,
      user_id: userStore?.user_id,
      channel_id: channelId,
      channel_code: channelCode,
      message: newMessage.trim(),
      file_path: "",
      time: new Date().toISOString(),
    };

    socket.emit(SOCKET_EVENTS.MESSAGE(channelCode), payload);
    fetchChannelMessages(channelId);
    setNewMessage("");
  }, [newMessage, userStore, channelId, channelCode, fetchChannelMessages]);

  const handleTyping = useCallback(() => {
    socket.emit(SOCKET_EVENTS.TYPING(channelCode), {
      username: userStore?.username,
      channel_code: channelCode,
    });
  }, [userStore?.username, channelCode]);

  const handleNavigate = useCallback(
    (section, originalIdx) => {
      setSelectedSectionIndex(originalIdx);
      navigate("/collaboration/" + section.channel_code);

      const code = `${window.location.hostname}_${section.channel_code}`;
      setChannelId(section.channel_id);
      setChannelCode(code);
      setSelectedChannelName(section.channel_name);
      setSelectedQuery(section.query);
      setChannelMembers(section.channel_members || []);
      fetchChannelMessages(section.channel_id);
      setShowConversation(true);
    },
    [navigate, fetchChannelMessages]
  );

  const timeLabel = (iso) => {
    const date = new Date(iso);
    const time = format(date, "hh:mm a");

    if (isToday(date)) {
      return `Today, ${time}`;
    } else if (isYesterday(date)) {
      return `Yesterday, ${time}`;
    } else {
      return format(date, "dd MMM yyyy, hh:mm a");
    }
  };

  // ShareModal handlers
  const handleOpenShareModal = () => {
    setPeopleOpenState(false); // Close the popover before opening the modal
    setShareTarget({
      channel_name: selectedChannelName,
      channel_id: channelId,
      query: selectedQuery,
    });
    setShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setShareTarget(null);
  };

  const handleShareConfirm = async (users) => {
    setAddingMembersLoading(true);
    try {
      const result = await addMembersToChannel(channelId, users);

      if (result?.success) {
        // Update the channel members state with the newly added members
        const newMembers = result.data;
        setChannelMembers((prevMembers) => [...prevMembers, ...newMembers]);

        const fresh = await getMyChannels(String(userStore?.user_id || ""));
        if (fresh?.success && Array.isArray(fresh.data)) {
          setMyChannels(fresh.data);
          const currentChannel = fresh.data.find(
            (ch) => String(ch.channel_id) === String(channelId)
          );
          if (currentChannel?.channel_members) {
            setChannelMembers(currentChannel.channel_members);
          }
        }

        setSnackbar({
          open: true,
          message: `Successfully added ${newMembers.length} user(s) to the channel!`,
          severity: "success",
        });
      } else {
        console.error("Failed to add members:", result?.error);
        setSnackbar({
          open: true,
          message: `Failed to add users: ${result?.error || "Unknown error"}`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error adding members to channel:", error);
      setSnackbar({
        open: true,
        message: `Error adding users: ${error.message}`,
        severity: "error",
      });
    } finally {
      setAddingMembersLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "row", width: "100%", minWidth: 0, height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)", bgcolor: "#ffffff", overflow: "hidden", boxSizing: "border-box" }}>
      {/* One card: Sidebar + Main */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          height: "100%",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          overflow: "hidden",
          bgcolor: "#ffffff",
        }}
      >
        {/* Sidebar */}
        <Box sx={{ width: 250, flexShrink: 0, borderRight: "1px solid #e2e8f0" }}>
          <SearchSavedQuerySidebar
            myChannels={myChannels}
            handleNavigate={handleNavigate}
            selectedSectionIndex={selectedSectionIndex}
            sidebarHeight="100%"
          />
        </Box>

      {/* Main */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <RootBox>
          <ChatArea>
            <ContentContainer>
              {/* Chat shell confines the popover */}
              <ChatShell ref={shellRef}>
                {/* Header (sticky) */}
                <HeaderRow>
                  <UsersTitle variant="h6" sx={{ flex: 1 }}>
                    {`${selectedChannelName} `}
                    {/* channelCode.split("_")[1] || "" */}
                  </UsersTitle>

                  {/* Owner pill */}
                  {channelOwner && (
                    <OwnerPill>
                      <OwnerAvatar
                        sx={{
                          bgcolor: pastelFrom(channelOwner.username),
                          color: "#0f172a",
                        }}
                      >
                        {(
                          channelOwner.first_name?.[0] ||
                          channelOwner.username?.[0] ||
                          "U"
                        ).toUpperCase()}
                      </OwnerAvatar>

                      <Box sx={{ minWidth: 0 }}>
                        <OwnerName noWrap>
                          {`${channelOwner.first_name || ""} ${
                            channelOwner.last_name || ""
                          }`.trim() || channelOwner.username}
                        </OwnerName>

                        <OwnerStatus noWrap>
                          <StatusDotInline online={ownerOnline} />
                          {ownerOnline ? "Available" : "Away"}
                        </OwnerStatus>
                      </Box>
                    </OwnerPill>
                  )}

                  {/* Right-side icons */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
                    <Tooltip title="Channel members">
                      <IconButton
                        ref={personIconRef}
                        size="small"
                        sx={{ color: "#64748b", width: 28, height: 28 }}
                        onClick={() => setPeopleOpenState(true)}
                      >
                        <PersonRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </HeaderRow>

                {/* ===== FULLSCREEN TOGGLE: SQL (black) ⟷ Conversation ===== */}
                {!showConversation ? (
                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
                      borderRadius: 0,
                      borderLeft: "3px solid #64748b",
                    }}
                  >
                    {/* Eye icon pinned on top-right of SQL area */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 10,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip title="Show Conversation">
                        <IconButton
                          size="small"
                          onClick={() => setShowConversation(true)}
                          sx={{
                            color: "#94A3B8",
                            "&:hover": { 
                              color: "#e2e8f0",
                              bgcolor: "rgba(255,255,255,0.1)",
                            },
                          }}
                        >
                          <VisibilityRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* SQL body grows to fill (no chat visible) */}
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "auto",
                        color: "#E2E8F0",
                        p: "8px 12px",
                        fontFamily:
                          "'JetBrains Mono','Fira Code','Menlo','Monaco','Ubuntu Mono',monospace",
                        fontSize: 11,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {formatSqlQuery(selectedQuery)}
                    </Box>

                  </Box>
                ) : (
                  /* ---------------- CONVERSATION FULLSCREEN -------------- */
                  <>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 1, pb: 0.25, bgcolor: "#ffffff" }}>
                      <Tooltip title="Show SQL">
                        <IconButton
                          size="small"
                          onClick={() => setShowConversation(false)}
                          sx={{
                            color: "#64748b",
                            "&:hover": { 
                              color: "#334155",
                              bgcolor: "#f1f5f9",
                            },
                          }}
                        >
                          <VisibilityRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Conversation container - chat view */}
                    <Box
                      sx={{
                        position: "relative",
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      {/* Messages */}
                      <ChatCard>
                        {messages.length === 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              py: 4,
                              textAlign: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 12,
                                bgcolor: "#f1f5f9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 1,
                              }}
                            >
                              <PersonRoundedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                            </Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                              No messages yet
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                              Type below to start the conversation
                            </Typography>
                          </Box>
                        )}
                        {messages.map((msg, idx) => {
                          const mine =
                            msg.sender_username === userStore?.username ||
                            msg.sender_username === userStore?.email;
                          const senderDisplayName =
                            getDisplayName(msg.sender_username);
                          return (
                            <ChatMsgRow key={idx} isMine={mine}>
                              {!mine && (
                                <ChatMsgAvatar
                                  sx={{
                                    bgcolor: pastelFrom(msg.sender_username),
                                    color: "#0f172a",
                                    fontWeight: 600,
                                  }}
                                >
                                  {(senderDisplayName?.[0] ||
                                    msg.sender_username?.[0] ||
                                    "U"
                                  ).toUpperCase()}
                                </ChatMsgAvatar>
                              )}

                              <ChatMsgBox as={ChatMsgCol} isMine={mine}>
                                {!mine && (
                                  <ChatMsgSender variant="caption">
                                    {senderDisplayName}
                                  </ChatMsgSender>
                                )}
                                <ChatMsgPaper isMine={mine} elevation={0}>
                                  <ChatMsgText variant="body1">
                                    {msg.message}
                                  </ChatMsgText>
                                </ChatMsgPaper>
                                <ChatMsgTime variant="caption" isMine={mine}>
                                  {timeLabel(msg.time)}
                                </ChatMsgTime>
                              </ChatMsgBox>
                            </ChatMsgRow>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </ChatCard>

                      <Divider sx={{ borderColor: "#e2e8f0" }} />

                      {/* Typing indicator */}
                      {Object.keys(typingUsers).filter(
                        (u) => u !== userStore?.username
                      ).length > 0 && (
                        <TypingIndicator>
                          <Typography variant="caption" color="textSecondary">
                            {Object.keys(typingUsers)
                              .filter((u) => u !== userStore?.username)
                              .map((u) => getDisplayName(u))
                              .join(", ")}{" "}
                            {Object.keys(typingUsers).length === 1
                              ? "is typing..."
                              : "are typing..."}
                          </Typography>
                        </TypingIndicator>
                      )}

                      {/* Input */}
                      <InputBar>
                        <InputBox>
                          <InputShell>
                            <InputField
                              fullWidth
                              size="small"
                              placeholder="Type a message..."
                              value={newMessage}
                              onChange={(e) => {
                                setNewMessage(e.target.value);
                                handleTyping();
                              }}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleSend()
                              }
                            />
                          </InputShell>
                          <SendButton
                            variant="contained"
                            onClick={handleSend}
                            aria-label="Send"
                          >
                            <SendRoundedIcon />
                          </SendButton>
                        </InputBox>
                      </InputBar>
                    </Box>
                  </>
                )}
                {/* ===== END TOGGLE ===== */}
              </ChatShell>
            </ContentContainer>

            {/* People popover (inside shell, under SQL/Chat) */}
            <PeoplePopover
              anchorEl={personIconRef.current}
              open={peopleOpenState}
              onClose={() => setPeopleOpenState(false)}
              members={channelMembers}
              connectedUsers={globalOnlineUsers}
              currentUsername={userStore?.username}
              containerRef={shellRef}
              onAddPeople={handleOpenShareModal}
              isOwner={isChannelOwner}
            />
          </ChatArea>
        </RootBox>
      </Box>
      </Box>

      {/* Page-level loading when adding members (modal already closed) */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "rgba(255,255,255,0.85)",
        }}
        open={addingMembersLoading}
      >
        <CircularProgress size={48} sx={{ color: "rgb(44, 52, 123)" }} />
      </Backdrop>

      {/* ShareModal for adding people */}
      <ShareModal
        open={shareModalOpen}
        onClose={handleCloseShareModal}
        onShare={handleShareConfirm}
        title={
          shareTarget
            ? `Add Member to "${shareTarget.channel_name}"`
            : "Add Member"
        }
        defaultSelectedEmails={[]}
        query_name={shareTarget?.channel_name}
        saved_query_id={shareTarget?.channel_id}
        ownerEmail={userStore?.email}
        ownerUserId={userStore?.user_id}
        excludeUserIds={channelMembers.map((m) => m.user_id).filter(Boolean)}
      />

      {/* Success/Error Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Collaboration;
